import React, { useEffect, useMemo, memo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoCloseOutline, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { useTutorial } from '../../context/TutorialContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { useSettings } from '../../context/SettingsContext';
import { createLogger } from '../../utils/logger-ssr-safe';

const logger = createLogger({ module: 'TutorialPanel', level: 'info' });

// Memoize the loading spinner to prevent unnecessary re-renders
const LoadingSpinner = memo(() => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
  </div>
));

// Memoize the error display
const ErrorDisplay = memo(() => (
  <div className="text-red-500 text-center p-4">
    Failed to load tutorial content
  </div>
));

// Memoize the empty state
const EmptyState = memo(() => (
  <div className="text-center p-4">No tutorial content available</div>
));

// Memoize the tutorial section content
const TutorialContent = memo(({ title, content, image }: { title: string, content: string, image?: string }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold">{title}</h3>
    <p className="text-gray-600 dark:text-gray-300">{content}</p>
    {image && (
      <img
        src={image}
        alt={title}
        className="rounded-lg shadow-md max-w-full"
      />
    )}
  </div>
));

// Memoize the progress dots
const ProgressDots = memo(({ totalSections, currentSection, goToSection }: { 
  totalSections: number, 
  currentSection: number, 
  goToSection: (index: number) => void 
}) => (
  <div className="flex justify-center space-x-2 mb-4">
    {Array.from({ length: totalSections }).map((_, index) => (
      <button
        key={index}
        onClick={() => goToSection(index)}
        className={`w-2 h-2 rounded-full transition-colors ${
          index === currentSection
            ? 'bg-blue-500'
            : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
        }`}
        aria-label={`Go to section ${index + 1}`}
      />
    ))}
  </div>
));

// Wrapper component that safely uses all hooks
const TutorialPanel: React.FC = () => {
  // Always call all hooks unconditionally
  const tutorial = useTutorial();
  const { darkMode } = useDarkMode(); 
  // We're not using settings but keeping the hook call to maintain consistency
  useSettings();
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Safe content access
  const currentContent = useMemo(() => {
    try {
      return tutorial.currentTutorial?.content?.sections?.[tutorial.currentSection] || null;
    } catch (err) {
      logger.error('Error accessing tutorial content:', err);
      return null;
    }
  }, [tutorial.currentTutorial, tutorial.currentSection]);
  
  // Right offset doesn't depend on sidebar collapsed anymore to avoid errors
  const rightOffset = 'lg:right-0';
  
  // Content rendering with error handling
  const contentNode = useMemo(() => {
    try {
      if (tutorial.loading) {
        return <LoadingSpinner />;
      }
      
      if (tutorial.error) {
        return <ErrorDisplay />;
      }
      
      if (!currentContent) {
        return <EmptyState />;
      }
      
      return (
        <TutorialContent 
          title={currentContent.title}
          content={currentContent.content}
          image={currentContent.image}
        />
      );
    } catch (err) {
      logger.error('Error rendering content:', err);
      return <ErrorDisplay />;
    }
  }, [tutorial.loading, tutorial.error, currentContent]);

  // Prevent body scrolling when panel is open
  useEffect(() => {
    if (!tutorial.isOpen) return;

    // Store original styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    const originalPosition = originalStyle.position;
    const originalHeight = originalStyle.height;
    const originalWidth = originalStyle.width;
    const originalTop = originalStyle.top;
    const originalScrollY = window.scrollY;

    // Apply styles to prevent scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${originalScrollY}px`;
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Restore original body styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
      
      // Restore scroll position
      window.scrollTo(0, originalScrollY);
    };
  }, [tutorial.isOpen]);

  // Handle touch events to prevent scrolling of background
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      // Only if tutorial is open and the touch target is outside the panel
      if (tutorial.isOpen && panelRef.current) {
        if (!panelRef.current.contains(e.target as Node)) {
          // Prevent default only for touches outside the panel
          e.preventDefault();
        }
      }
    };

    // Use passive: false to allow preventDefault()
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [tutorial.isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!tutorial.isOpen) return;
    
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        try {
          tutorial.closeTutorial();
        } catch (err) {
          logger.error('Error closing tutorial:', err);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [tutorial]);

  // Don't render if tutorial is not open
  if (!tutorial.isOpen) {
    return null;
  }

  // Wrap interactions in try-catch to prevent crashes
  const safeNextSection = () => {
    try {
      tutorial.nextSection();
    } catch (err) {
      logger.error('Error navigating to next section:', err);
    }
  };

  const safePreviousSection = () => {
    try {
      tutorial.previousSection();
    } catch (err) {
      logger.error('Error navigating to previous section:', err);
    }
  };

  const safeGoToSection = (index: number) => {
    try {
      tutorial.goToSection(index);
    } catch (err) {
      logger.error('Error navigating to section:', err);
    }
  };

  const safeCloseTutorial = () => {
    try {
      tutorial.closeTutorial();
    } catch (err) {
      logger.error('Error closing tutorial:', err);
    }
  };

  return (
    <AnimatePresence>
      {/* Background overlay to capture touches and prevent background scrolling */}
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[10003]"
        onClick={safeCloseTutorial}
      />
      
      <motion.div
        ref={panelRef}
        key="panel"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className={`
          fixed top-[72px] ${rightOffset} bottom-0
          w-full lg:w-96
          ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
          shadow-lg
          z-[10004]
          flex flex-col
          overflow-hidden
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold">{tutorial.currentTutorial?.title}</h2>
          <button
            onClick={safeCloseTutorial}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close tutorial"
          >
            <IoCloseOutline size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 overscroll-contain">
          {contentNode}
        </div>

        {/* Navigation */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {/* Progress dots */}
          <ProgressDots 
            totalSections={tutorial.totalSections}
            currentSection={tutorial.currentSection}
            goToSection={safeGoToSection}
          />

          {/* Navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={safePreviousSection}
              disabled={tutorial.currentSection === 0}
              className={`
                flex items-center px-4 py-2 rounded-lg
                ${tutorial.currentSection === 0
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900'
                }
              `}
            >
              <IoChevronBack size={20} className="mr-1" />
              Previous
            </button>
            {tutorial.currentSection === tutorial.totalSections - 1 ? (
              <button
                onClick={safeCloseTutorial}
                className="flex items-center px-4 py-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900"
              >
                Close
                <IoCloseOutline size={20} className="ml-1" />
              </button>
            ) : (
              <button
                onClick={safeNextSection}
                className="flex items-center px-4 py-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900"
              >
                Next
                <IoChevronForward size={20} className="ml-1" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default memo(TutorialPanel); 